// Compact SLA status pill derived from a ticket's server-computed `sla_state`.
// Shows the most relevant clock: the first-response goal until it's met, then
// the resolution goal. Green = on track, amber = due within an hour, red =
// overdue or missed.
import React from 'react';
import { Clock, WarningCircle, CheckCircle } from '@phosphor-icons/react';

function fmt(mins) {
  const m = Math.abs(Math.round(mins));
  if (m < 60) return `${m}m`;
  if (m < 1440) return `${Math.round(m / 60)}h`;
  return `${Math.round(m / 1440)}d`;
}

export default function SlaBadge({ slaState }) {
  if (!slaState) return null;
  const r = slaState.response;
  const res = slaState.resolution;
  // A completed resolution is the final outcome (ticket resolved/closed) — show
  // its met/missed state even if the response clock was never stopped. Otherwise
  // show the response clock until it's met, then fall through to resolution.
  let pick, kind;
  if (res && res.completed) { pick = res; kind = 'Resolve'; }
  else if (!r || r.completed) { pick = res; kind = 'Resolve'; }
  else { pick = r; kind = 'Response'; }
  if (!pick) return null;

  let tone, Icon, label;
  if (pick.completed) {
    if (pick.met) {
      // SLA met — show it (green) instead of leaving the cell blank.
      tone = 'bg-emerald-50 text-emerald-700 ring-emerald-200'; Icon = CheckCircle; label = `${kind} met`;
    } else {
      tone = 'bg-red-50 text-red-700 ring-red-200'; Icon = WarningCircle; label = `${kind} missed`;
    }
  } else if (pick.breached) {
    tone = 'bg-red-50 text-red-700 ring-red-200'; Icon = WarningCircle; label = `${kind} overdue ${fmt(pick.remaining_mins)}`;
  } else {
    const atRisk = pick.remaining_mins <= 60;
    tone = atRisk ? 'bg-amber-50 text-amber-700 ring-amber-200' : 'bg-emerald-50 text-emerald-700 ring-emerald-200';
    Icon = Clock; label = `${kind} ${fmt(pick.remaining_mins)} left`;
  }
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded ring-1 ${tone}`} data-testid="sla-badge">
      <Icon size={12} weight="fill" /> {label}
    </span>
  );
}
